<template>
  <v-container fluid>
    <v-row v-if="isVolumeBased">
      <v-col cols="12" class="mb-2">
        <CruiseDataTable
        v-if="!loading"
        :propCruiseData.sync="volumeBasedCruiseData"
        :tractTypeSpecialization="TractTypeSpecialization.forInt(tract.type?.specialization)"
        @edit="c => editCruiseData(c)"
        @delete="confirmDeleteCruiseData"
        @allocate="openAllocations"
        isVolumeBased
        :units="units">
          <template #top>
            <v-row>
              <v-col>
                <span class="title font-weight-bold ml-3" style="color: rgba(0, 0, 0, 0.6)">{{$t('species')}}</span>
                <Icon
                margin="mb-1 ml-1"
                :small="false"
                icon="mdi-plus"
                :tooltipText="$t('newCruiseData')"
                :disabled="scalingRegionNotDefined"
                elementId="cruise-add-species-button"
                @icon-clicked="createCruiseData(woodTypeEnum.Softwood)"/>
                <span v-if="requirementHint" class="caption text--secondary ml-2">{{ requirementHint }}</span>
              </v-col>
              <v-spacer/>
              <v-chip small class="mt-3">
                <span class="font-weight-bold" style="color: rgba(0, 0, 0, 0.6)">
                  {{scalingRegionNotDefined ? $t('noScalingRegionSet') : ScalingRegion.fromInt(tract.scalingRegion)}}
                </span>
              </v-chip>
              <v-col cols="auto">
                <Icon
                v-if="tract.tractGroupId != null"
                margin="mr-1"
                :small="false"
                icon="mdi-content-copy"
                :tooltipText="volumeCopyTooltipText"
                :iconColor="volumeCopyDisabled ? 'grey' : 'primary'"
                :overrideClickable="volumeCopyDisabled"
                elementId="cruise-copy-volume"
                @icon-clicked="openCopyDialog"/>
                <Icon
                :small="false"
                icon="mdi-pine-tree"
                iconColor="primary"
                :tooltipText="$t('viewProductCruise')"
                @icon-clicked="toggleIsVolumeBased"
                elementId="toggle-product-cruise"
                />
              </v-col>
            </v-row>
          </template>
        </CruiseDataTable>
      </v-col>
      <v-col cols="12" v-if="!loading && volumeBasedCruiseData.length > 0">
        <v-expansion-panels class="narrow-expansion-panels pb-2">
          <v-expansion-panel lazy>
            <v-expansion-panel-header class="pa-0">
              <template v-slot:actions>
                <v-icon class="mr-3">mdi-chevron-down</v-icon>
              </template>
              <span class="subtitle font-weight-bold ml-3 mb-0" style="color: rgba(0, 0, 0, 0.6)">{{ $t('speciesRemainingByDestination') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <AllocationChart
                :volumeBasedCruiseData="volumeBasedCruiseData"
                style="width: 90%"
                class="mx-auto"
              />
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-col>
    </v-row>
    <v-row dense no-gutters v-if="!isVolumeBased">
      <v-col cols="12" class="mb-2">
        <CruiseDataTable
        v-if="!loading"
        :propCruiseData.sync="splitCruiseData.softwood"
        :tractTypeSpecialization="TractTypeSpecialization.forInt(tract.type?.specialization)"
        @edit="c => editCruiseData(c, woodTypeEnum.Softwood)"
        @delete="confirmDeleteCruiseData"
        :units="units">
          <template #top>
            <v-row dense no-gutters>
              <v-col>
                <span class="title font-weight-bold ml-3" style="color: rgba(0, 0, 0, 0.6)">{{$t('softwood')}}</span>
                <Icon
                margin="mb-1 ml-1"
                :small="false"
                icon="mdi-plus"
                :tooltipText="$t('newCruiseData')"
                elementId="cruise-add-softwood-button"
                @icon-clicked="createCruiseData(woodTypeEnum.Softwood)"/>
                <span v-if="requirementHint" class="caption text--secondary ml-2">{{ requirementHint }}</span>
              </v-col>
              <v-col cols="auto">
                <Icon
                :small="false"
                :icon="units === HarvestUnits.MBF ? 'mdi-scale' : 'mdi-cube'"
                iconColor="primary"
                :tooltipText="units === HarvestUnits.MBF ? $t('viewWeight') : $t('viewVolume')"
                @icon-clicked="toggleVolumeView"
                elementId="toggle-volume"
                margin="mr-2"
                />
              </v-col>
              <v-col cols="auto">
                <Icon
                v-if="tract.tractGroupId != null"
                margin="mr-2"
                :small="false"
                icon="mdi-content-copy"
                :tooltipText="productCopyTooltipText"
                :iconColor="productCopyDisabled ? 'grey' : 'primary'"
                :overrideClickable="productCopyDisabled"
                elementId="cruise-copy-product"
                @icon-clicked="openCopyDialog"
                />
                <Icon
                :small="false"
                icon="mdi-ruler-square"
                iconColor="primary"
                :tooltipText="$t('viewSpeciesCruise')"
                @icon-clicked="toggleIsVolumeBased"
                elementId="toggle-species-cruise"
                />
              </v-col>
            </v-row>
          </template>
        </CruiseDataTable>
      </v-col>
      <v-col cols="12" class="mb-3">
        <CruiseDataTable
        v-if="!loading"
        :propCruiseData.sync="splitCruiseData.hardwood"
        @edit="c => editCruiseData(c, woodTypeEnum.Hardwood)"
        :tractTypeSpecialization="TractTypeSpecialization.forInt(tract.type?.specialization)"
        @delete="confirmDeleteCruiseData"
        :units="units">
          <template #top>
            <span class="title font-weight-bold ml-3" style="color: rgba(0, 0, 0, 0.6)">{{$t('hardwood')}}</span>
            <Icon
            margin="mb-1 ml-1"
            :small="false"
            icon="mdi-plus"
            :tooltipText="$t('newCruiseData')"
            elementId="cruise-add-hardwood-button"
            @icon-clicked="createCruiseData(woodTypeEnum.Hardwood)"/>
          </template>
        </CruiseDataTable>
      </v-col>
      <v-col cols="8"/>
      <v-col cols="4">
        <v-row dense no-gutters>
          <v-col cols="12">
            <span class="title font-weight-bold ml-3">{{$t('totals')}}</span>
          </v-col>
          <v-col cols="12">
            <v-simple-table dense>
              <tbody>
                <tr v-for="total of totals" :key="total.text">
                  <td><span class="font-weight-bold">{{total.text}}</span></td>
                  <td style="text-align: right" class="font-weight-bold">{{total.value}}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row>
      <v-dialog v-model="cruiseDataForm" width="80vh">
        <CruiseDataForm
        v-if="creating || editing"
        :tract="tract"
        :editing="editing"
        :propCruiseData="focusedCruiseData"
        :woodType="formWoodType"
        :isVolumeBased="isVolumeBased"
        :units="units"
        @close="closeForm(false)"
        @cruise-data-changed="onCruiseDataChanged"/>
      </v-dialog>
      <Dialog :stateId="accrualDialogId" @close="resetAccrualPayment">
        <TractPaymentForm
        v-if="accrualSeed"
        :tractId="tract.tractId"
        :performanceBonds="[]"
        isObligationTract
        :seedPayment="accrualSeed"
        @close="closeDialogsAtOrAbove(accrualDialogId)"
        @submitted="onAccrualPaymentSubmitted"/>
      </Dialog>
      <v-dialog
      width="400px"
      v-model="deleting">
        <ConfirmDelete
        :title="$t('cruiseData')"
        v-if="deleting"
        @delete="deleteItem"
        @cancel-delete="closeForm(false)"/>
      </v-dialog>
      <v-dialog v-model="allocationDialog" width="700px">
        <TractContentAllocations
          v-if="allocatingTractContent"
          :tractContent="allocatingTractContent"
          :tractId="tract.tractId"
          @close="closeAllocations"
          @allocations-changed="onAllocationsChanged"
        />
      </v-dialog>
      <v-dialog v-model="copyDialogOpen" width="500px">
        <CopyFromTractDialog
        v-if="copyDialogOpen"
        :tracts="otherTractsInGroup"
        :loading="copying"
        :warningText="copySkipWarningText"
        @confirm="copyFromTract"
        @cancel="closeCopyDialog"
        />
      </v-dialog>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { scatterToObject } from '@/utils/base'
import { WoodType, HarvestUnits, ScalingRegion, TractTypeSpecialization, TractPayableSpecialization, UserClaims } from '@/utils/Enumerations'
import { cruiseDataTotals } from '@/components/tract/cruise-data/CruiseDataTotals'
import { formatMoney } from '@/utils/NumericMutations'
import CruiseDataHeaders from '@/headers/Composition'
import { LocalStorageKeys } from '@/utils/LocalStorageActor'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'CruiseData',

  components: {
    CruiseDataTable: () => import('@/components/tract/cruise-data/CruiseDataTable.vue'),
    CruiseDataForm: () => import('@/components/tract/cruise-data/CruiseDataForm.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    TractContentAllocations: () => import('@/components/tract/cruise-data/TractContentAllocations.vue'),
    AllocationChart: () => import('@/components/tract/cruise-data/AllocationChart.vue'),
    CopyFromTractDialog: () => import('@/components/tract/CopyFromTractDialog.vue'),
    TractPaymentForm: () => import('@/components/tract/tract-payables/TractPaymentForm.vue'),
    Dialog: () => import('@/components/Dialog.vue')
  },

  props: {
    tract: Object,
    flat: Boolean,
    showImmediately: { type: Boolean, default: false },
    refreshCruiseData: { type: Boolean, default: false },
    requirementHint: { type: String, default: '' }
  },

  watch: {
    cruiseDataForm (val) {
      if (!val) {
        this.closeForm(false)
      }
    },

    allocationDialog (val) {
      if (!val) {
        this.closeAllocations()
      }
    },

    refreshCruiseData (val) {
      if (val) {
        this.refreshTractContent(this.tract.tractId)
      }
    }
  },

  data: () => ({
    TractTypeSpecialization,
    HarvestUnits,
    creating: false,
    editing: false,
    cruiseDataForm: false,
    isVolumeBased: localStorage.getItem(LocalStorageKeys.TRACT_CRUISE_IS_VOLUME_BASED) === 'true',
    formWoodType: undefined,
    deleting: false,
    loading: true,
    productBasedCruiseData: [],
    volumeBasedCruiseData: [],
    focusedCruiseData: null,
    units: HarvestUnits.forInt(parseInt(localStorage.getItem(LocalStorageKeys.TRACT_CRUISE_UNITS))) ?? HarvestUnits.Tons,
    ScalingRegion,
    allocatingTractContent: null,
    allocationDialog: false,
    UserClaims,
    copyDialogOpen: false,
    copying: false,
    tractGroup: null,
    accrualDialogId: uniqueDialogId('cruise-accrual-payment'),
    accrualSeed: null
  }),

  computed: {
    splitCruiseData () {
      return scatterToObject(this.productBasedCruiseData, {
        softwood: d => d.woodType === WoodType.Softwood.value,
        hardwood: d => d.woodType === WoodType.Hardwood.value
      })
    },

    totals () {
      const cdTotals = cruiseDataTotals(this.productBasedCruiseData, this.units)
      return Object.keys(cdTotals).map(key => ({
        text: CruiseDataHeaders.compositionHeaders(this.units).find(cdh => cdh.value.includes(key))?.text,
        value: key === 'cruisedValue' ? formatMoney(cdTotals[key]) : cdTotals[key]
      }))
    },

    woodTypeEnum () { return WoodType },

    scalingRegionNotDefined () {
      return this.tract.scalingRegion == null || this.tract.scalingRegion === ScalingRegion.NotDefined.value
    },

    hasVolumeBasedCruiseData () {
      return this.volumeBasedCruiseData.length > 0
    },

    hasProductBasedCruiseData () {
      return this.productBasedCruiseData.length > 0
    },

    otherTractsInGroup () {
      if (!this.tractGroup?.tracts) return []
      return this.tractGroup.tracts.filter(t => t.tractId !== this.tract.tractId)
    },

    volumeCopyDisabled () {
      return !userAssignedClaim(UserClaims.ContractManager) || this.hasVolumeBasedCruiseData || this.scalingRegionNotDefined
    },

    volumeCopyTooltipText () {
      if (!userAssignedClaim(UserClaims.ContractManager)) return this.$t('copyDisabledNoPermission')
      if (this.hasVolumeBasedCruiseData) return this.$t('copyDisabledVolumeCruiseDataExists')
      if (this.scalingRegionNotDefined) return this.$t('copyDisabledNoScalingRegion')
      return this.$t('copyFromAnotherTract')
    },

    productCopyDisabled () {
      return !userAssignedClaim(UserClaims.ContractManager) || this.hasProductBasedCruiseData
    },

    productCopyTooltipText () {
      if (!userAssignedClaim(UserClaims.ContractManager)) return this.$t('copyDisabledNoPermission')
      if (this.hasProductBasedCruiseData) return this.$t('copyDisabledProductCruiseDataExists')
      return this.$t('copyFromAnotherTract')
    },

    copySkipWarningText () {
      if (this.isVolumeBased && this.productCopyDisabled && this.hasProductBasedCruiseData) {
        return this.$t('copySkipProductDataExists')
      }
      if (!this.isVolumeBased && this.volumeCopyDisabled) {
        if (this.scalingRegionNotDefined) return this.$t('copySkipNoScalingRegion')
        if (this.hasVolumeBasedCruiseData) return this.$t('copySkipSpeciesDataExists')
      }
      return null
    }
  },

  async created () {
    await this.refreshTractContent(this.tract.tractId, this.showImmediately)
  },

  methods: {
    formatMoney,
    ...mapActions('tract-contents', ['fetchTractContents', 'createTractContent', 'deleteTractContent']),
    ...mapActions('tract', ['fetchTractGroup']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    userAssignedClaim,
    async deleteItem () {
      const requestObj = {
        tractId: this.tract.tractId,
        tractContentId: this.focusedCruiseData.tractContentId
      }

      await this.deleteTractContent(requestObj)
      this.closeForm(true)
    },

    createCruiseData (woodType) {
      this.closeForm(false)
      this.formWoodType = woodType

      this.creating = true
      this.cruiseDataForm = true
    },

    editCruiseData (cruiseData, woodType = undefined) {
      this.focusedCruiseData = cruiseData
      this.formWoodType = woodType
      this.editing = true
      this.cruiseDataForm = true
    },

    async refreshTractContent (tractId, showImmediately) {
      this.loading = true
      try {
        const [volumeData, productData] = await Promise.all([
          this.fetchTractContents({ tractId, isVolumeBased: true }),
          this.fetchTractContents({ tractId, isVolumeBased: false })
        ])
        this.volumeBasedCruiseData = volumeData
        this.productBasedCruiseData = productData
      } finally {
        this.$emit('tract-contents-updated', [...this.productBasedCruiseData, ...this.volumeBasedCruiseData])
        this.$emit('cruise-data-refreshed')
        this.loading = false
        if (showImmediately) {
          this.createCruiseData()
        }
      }
    },

    confirmDeleteCruiseData (tractContent) {
      this.focusedCruiseData = tractContent
      this.deleting = true
    },

    toggleIsVolumeBased () {
      this.isVolumeBased = !this.isVolumeBased
      localStorage.setItem(LocalStorageKeys.TRACT_CRUISE_IS_VOLUME_BASED, this.isVolumeBased.toString())
    },

    async closeForm (refresh) {
      if (refresh) {
        await this.refreshTractContent(this.tract.tractId)
        this.$emit('cruise-data-changed')
      }

      this.cruiseDataForm = false
      this.deleting = false
      this.creating = false
      this.editing = false
      this.focusedCruiseData = null
    },

    async onCruiseDataChanged (accrualPrompt) {
      await this.closeForm(true)
      if (!accrualPrompt) return

      this.accrualSeed = {
        specialization: TractPayableSpecialization.Adjustment.value,
        tractContentId: accrualPrompt.tractContentId,
        preIssued: true
      }
      this.openOrUpdateDialog({ id: this.accrualDialogId, width: '1200px' })
    },

    async onAccrualPaymentSubmitted () {
      this.closeDialogsAtOrAbove(this.accrualDialogId)
      await this.refreshTractContent(this.tract.tractId)
      this.$emit('cruise-data-changed')
    },

    resetAccrualPayment () {
      this.accrualSeed = null
    },

    toggleVolumeView () {
      switch (this.units) {
        case HarvestUnits.Tons: this.units = HarvestUnits.MBF; break
        default: this.units = HarvestUnits.Tons
      }
      localStorage.setItem(LocalStorageKeys.TRACT_CRUISE_UNITS, this.units.value)
    },

    openAllocations (tractContent) {
      this.allocatingTractContent = tractContent
      this.allocationDialog = true
    },

    closeAllocations () {
      this.allocationDialog = false
      this.allocatingTractContent = null
    },

    async onAllocationsChanged () {
      await this.refreshTractContent(this.tract.tractId)
    },

    async openCopyDialog () {
      if (this.isVolumeBased ? this.volumeCopyDisabled : this.productCopyDisabled) return
      if (!this.tractGroup) {
        this.tractGroup = await this.fetchTractGroup(this.tract.tractGroupId)
      }
      this.copyDialogOpen = true
    },

    closeCopyDialog () {
      this.copyDialogOpen = false
    },

    async copyFromTract (sourceTract) {
      this.copying = true
      try {
        const [sourceVolumeContents, sourceProductContents] = (await Promise.all([
          this.fetchTractContents({ tractId: sourceTract.tractId, isVolumeBased: true }),
          this.fetchTractContents({ tractId: sourceTract.tractId, isVolumeBased: false })
        ])).map(arr => [...arr])

        if (!this.volumeCopyDisabled) {
          for (const content of sourceVolumeContents) {
            await this.createTractContent({
              tractId: this.tract.tractId,
              speciesId: content.speciesId,
              productId: null,
              pricePerUnit: content.pricePerUnit || 0,
              obligationRate: content.obligationRate || 0,
              expected: 0,
              expectedMBF: 0,
              adjustment: 0,
              adjustmentMBF: 0
            })
          }
        }

        if (!this.productCopyDisabled) {
          for (const content of sourceProductContents) {
            await this.createTractContent({
              tractId: this.tract.tractId,
              productId: content.productId,
              speciesId: null,
              pricePerUnit: content.pricePerUnit || 0,
              expected: 0,
              expectedMBF: 0,
              adjustment: 0,
              adjustmentMBF: 0
            })
          }
        }

        this.$emit('cruise-data-changed')
        this.setSnack(this.$t('copySuccess', { name: sourceTract.name }))
        this.closeCopyDialog()
        await this.refreshTractContent(this.tract.tractId)
      } finally {
        this.copying = false
      }
    }
  }
}
</script>
